import { useState } from "react";
import api from "@/lib/api";
import { X, Search, Crosshair, Navigation, MapPin } from "lucide-react";
import { toast } from "sonner";

const INP = "bg-secondary/60 border border-border rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-[#17A2B8] w-full";

const poiToDest = (p) => {
  if (p.type === "polygon" && p.coordinates?.length) {
    const n = p.coordinates.length;
    return { lat: p.coordinates.reduce((s, c) => s + c.lat, 0) / n, lon: p.coordinates.reduce((s, c) => s + c.lon, 0) / n, label: `POI · ${p.name}` };
  }
  return { lat: p.lat, lon: p.lon, label: `POI · ${p.name}` };
};

export default function RouteToolPanel({ pois = [], selected, onRoute, onClose }) {
  const [mode, setMode] = useState("address");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [dest, setDest] = useState(null);
  const [latI, setLatI] = useState("");
  const [lonI, setLonI] = useState("");
  const [poiId, setPoiId] = useState("");
  const [nearest, setNearest] = useState([]);
  const [nearestTarget, setNearestTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (query.trim().length < 2) return;
    setBusy(true);
    try { const r = await api.get(`/geocode/search?q=${encodeURIComponent(query)}`); setResults(r.data); if (!r.data.length) toast.info("Aucune adresse trouvée"); }
    catch { toast.error("Recherche indisponible"); } finally { setBusy(false); }
  };

  const setCoordDest = () => {
    const la = parseFloat(latI), lo = parseFloat(lonI);
    if (isNaN(la) || isNaN(lo)) return toast.error("Coordonnées invalides");
    setDest({ lat: la, lon: lo, label: `${la.toFixed(5)}, ${lo.toFixed(5)}` });
  };

  const doRoute = async (from, target, label) => {
    if (!from || !target) return;
    setBusy(true);
    try {
      const r = await api.get(`/route?from_lat=${from.lat}&from_lon=${from.lon}&to_lat=${target.lat}&to_lon=${target.lon}`);
      onRoute(r.data.geometry, { distance_km: r.data.distance_km, duration_min: r.data.duration_min }, target);
      toast.success(`Itinéraire ${label ? "depuis " + label + " " : ""}· ${r.data.distance_km} km · ~${r.data.duration_min} min`);
      onClose();
    } catch { toast.error("Itinéraire indisponible"); } finally { setBusy(false); }
  };

  const findNearest = async (target, targetLabel) => {
    if (!target) return toast.error("Choisissez d'abord une destination");
    setBusy(true);
    try {
      const url = target.exclude ? `/nearest-vehicles?lat=${target.lat}&lon=${target.lon}&exclude=${target.exclude}&limit=5` : `/nearest-vehicles?lat=${target.lat}&lon=${target.lon}&limit=5`;
      const r = await api.get(url);
      setNearest(r.data.map((u) => ({ ...u, _from: targetLabel })));
      setNearestTarget(target);
      if (!r.data.length) toast.info("Aucune unité localisée");
    } catch { toast.error("Erreur"); } finally { setBusy(false); }
  };

  return (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[620] w-[92vw] max-w-[420px] glass rounded-lg p-4 animate-slidein" data-testid="route-tool-panel" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-3">
        <p className="font-bold text-sm flex items-center gap-2"><Crosshair className="w-4 h-4 brand-orange" /> Itinéraire & véhicule le plus proche</p>
        <button onClick={onClose} data-testid="route-tool-close" className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
      </div>

      <div className="flex gap-1 mb-3 bg-secondary/40 p-1 rounded-md">
        {[["address", "Adresse"], ["coord", "Coordonnée"], ["poi", "POI"]].map(([k, l]) => (
          <button key={k} onClick={() => { setMode(k); setDest(null); setResults([]); }} data-testid={`route-mode-${k}`}
            className={`flex-1 py-1.5 rounded text-xs font-semibold transition-colors ${mode === k ? "bg-[#17A2B8] text-white" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {mode === "address" && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input className={INP} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Ex: Rue de l'Alma, Nouméa" data-testid="route-address-input" />
            <button onClick={search} disabled={busy} data-testid="route-address-search" className="bg-[#17A2B8] hover:bg-[#1391a5] text-white px-3 rounded-md"><Search className="w-4 h-4" /></button>
          </div>
          {results.length > 0 && (
            <div className="max-h-32 overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="route-address-results">
              {results.map((r, i) => (
                <button key={i} onClick={() => { setDest({ lat: r.lat, lon: r.lon, label: r.label }); setResults([]); }} data-testid={`route-address-result-${i}`}
                  className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-black/5 truncate">{r.label}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === "coord" && (
        <div className="flex gap-2">
          <input className={INP} value={latI} onChange={(e) => setLatI(e.target.value)} placeholder="Latitude" data-testid="route-lat" />
          <input className={INP} value={lonI} onChange={(e) => setLonI(e.target.value)} placeholder="Longitude" data-testid="route-lon" />
          <button onClick={setCoordDest} data-testid="route-coord-set" className="bg-[#17A2B8] hover:bg-[#1391a5] text-white px-3 rounded-md text-xs font-semibold">OK</button>
        </div>
      )}

      {mode === "poi" && (
        <select className={INP} value={poiId} onChange={(e) => { setPoiId(e.target.value); const p = pois.find((x) => x.id === e.target.value); if (p) setDest(poiToDest(p)); }} data-testid="route-poi-select">
          <option value="">— choisir un POI —</option>
          {pois.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      )}

      {dest && (
        <div className="mt-3 text-xs flex items-start gap-1.5 bg-secondary/40 rounded-md p-2" data-testid="route-dest">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 brand-orange" /><span className="truncate">{dest.label}</span>
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 gap-2">
        <button onClick={() => findNearest(dest, "de la destination")} disabled={busy || !dest} data-testid="route-nearest-to-dest"
          className="flex items-center justify-center gap-2 bg-[#17A2B8]/15 hover:bg-[#17A2B8]/25 border border-[#17A2B8]/40 text-[#2ec5db] font-semibold py-2 rounded-md text-xs disabled:opacity-40">
          <Crosshair className="w-3.5 h-3.5" /> Unité la plus proche de la destination
        </button>
        {selected?.last_position && dest && (
          <button onClick={() => doRoute({ lat: selected.last_position.lat, lon: selected.last_position.lon }, dest, selected.name)} disabled={busy} data-testid="route-from-selected"
            className="flex items-center justify-center gap-2 bg-[#F5821F]/15 hover:bg-[#F5821F]/25 border border-[#F5821F]/40 text-[#F5821F] font-semibold py-2 rounded-md text-xs">
            <Navigation className="w-3.5 h-3.5" /> Itinéraire depuis {selected.name}
          </button>
        )}
        {selected?.last_position && (
          <button onClick={() => findNearest({ lat: selected.last_position.lat, lon: selected.last_position.lon, exclude: selected.id }, selected.name)} disabled={busy} data-testid="route-nearest-to-selected"
            className="flex items-center justify-center gap-2 bg-secondary/60 hover:bg-secondary border border-border font-semibold py-2 rounded-md text-xs">
            <Crosshair className="w-3.5 h-3.5" /> Unités proches de {selected.name}
          </button>
        )}
      </div>

      {nearest.length > 0 && (
        <div className="mt-3" data-testid="route-nearest-list">
          <p className="label-mono mb-1.5">Plus proches {nearest[0]?._from ? `(${nearest[0]._from})` : ""} — cliquez pour l'itinéraire</p>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {nearest.map((u, i) => (
              <button key={u.id} onClick={() => doRoute({ lat: u.lat, lon: u.lon }, nearestTarget, u.name)} data-testid={`route-nearest-${i}`}
                className="w-full flex items-center justify-between text-xs bg-secondary/40 hover:bg-secondary rounded px-2.5 py-1.5">
                <span className="font-semibold flex items-center gap-1.5"><span className="w-5 text-center" style={{ color: i === 0 ? "#F5821F" : "#71717A" }}>{i + 1}</span>{u.name}</span>
                <span className="data text-muted-foreground">{u.distance_km} km</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
